import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, AlertTriangle, Pause, Activity, DollarSign, AlertOctagon } from "lucide-react";

const F = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toFixed(d));

export default function RiskGuardrails() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    daily_loss_cap_pct: "",
    max_drawdown_pct: "",
    max_position_pct: "",
    max_symbols: "",
    auto_pause_bots: true,
  });
  const [saving, setSaving] = useState(false);
  const [failover, setFailover] = useState({ primary: "sim", fallback: "sim", auto_failover: true });
  const [realMoney, setRealMoney] = useState({ enabled: false });
  const [rmCap, setRmCap] = useState(500);

  const loadRealMoney = () => api.get("/real-money/status").then((r) => setRealMoney(r.data)).catch(() => {});

  const load = () => {
    api.get("/risk/portfolio-limits").then((r) => {
      setData(r.data);
      const l = r.data.limits || {};
      setForm({
        daily_loss_cap_pct: l.daily_loss_cap_pct ?? "",
        max_drawdown_pct: l.max_drawdown_pct ?? "",
        max_position_pct: l.max_position_pct ?? "",
        max_symbols: l.max_symbols ?? "",
        auto_pause_bots: l.auto_pause_bots ?? true,
      });
    }).catch(() => {});
    api.get("/broker/failover").then((r) => setFailover(r.data)).catch(() => {});
    loadRealMoney();
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const num = (v) => v === "" || v === null ? null : Number(v);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/risk/portfolio-limits", {
        daily_loss_cap_pct: num(form.daily_loss_cap_pct),
        max_drawdown_pct: num(form.max_drawdown_pct),
        max_position_pct: num(form.max_position_pct),
        max_symbols: form.max_symbols === "" ? null : parseInt(form.max_symbols),
        auto_pause_bots: form.auto_pause_bots,
      });
      toast.success("Risk limits saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveFailover = async () => {
    try {
      await api.patch("/broker/failover", failover);
      toast.success("Broker failover updated");
    } catch (e) { toast.error("Save failed"); }
  };

  const enableRealMoney = async () => {
    const confirm1 = window.confirm(
      "REAL MONEY MODE\n\nThis lets bots + webhooks place actual live orders through your connected broker. " +
      "Losses will be real. Continue?"
    );
    if (!confirm1) return;
    const capStr = window.prompt("Daily notional cap in USD (safety limit):", String(rmCap));
    if (!capStr) return;
    try {
      const { data } = await api.post("/real-money/enable", {
        acknowledged: true,
        max_daily_notional_usd: parseFloat(capStr),
      });
      toast.success("Real-money mode ENABLED", {
        description: `Webhook secret: ${data.webhook_secret.slice(0, 12)}… — copy from Settings.`,
        duration: 10000,
      });
      loadRealMoney();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enable failed"); }
  };

  const disableRealMoney = async () => {
    if (!window.confirm("Disable real-money mode? Bots will drop back to paper.")) return;
    try {
      await api.post("/real-money/disable");
      toast.success("Real-money mode disabled");
      loadRealMoney();
    } catch { toast.error("Disable failed"); }
  };

  const status = data?.status || {};
  const halted = status.halted;
  const triggers = status.triggers || [];

  return (
    <div className="space-y-6" data-testid="risk-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7" />
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Portfolio protection</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-0.5">Risk Guardrails</h1>
        </div>
      </div>

      {/* Status panel */}
      <Card className={`p-5 rounded-2xl border-2 ${halted ? "border-[hsl(var(--loss))]" : "border-border"}`} data-testid="risk-status">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Equity" val={data ? `$${status.equity?.toLocaleString()}` : "…"} />
          <Stat label="Today's P&L" val={data ? `${status.intraday_pnl_pct >= 0 ? "+" : ""}${F(status.intraday_pnl_pct)}%` : "…"}
                cls={status.intraday_pnl_pct >= 0 ? "gain" : "loss"} />
          <Stat label="Drawdown" val={data ? `${F(status.drawdown_pct)}%` : "…"} cls="loss" />
          <Stat label="Open positions" val={data ? status.symbols_open : "…"} />
        </div>
        {halted && (
          <div className="mt-4 p-3 rounded-lg bg-[hsl(var(--loss))]/10 border border-[hsl(var(--loss))]/40 flex items-center gap-3" data-testid="risk-halted-banner">
            <AlertTriangle className="w-5 h-5 text-[hsl(var(--loss))]" />
            <div className="text-sm">
              <div className="font-medium">All bots halted — {triggers.join(", ")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Lift the trigger or edit your limits below, then restart bots from the Auto-Trade page.</div>
            </div>
          </div>
        )}
      </Card>

      {/* Limits editor */}
      <Card className="p-5 rounded-2xl" data-testid="risk-limits-editor">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-display font-semibold">Portfolio limits</div>
            <div className="text-xs text-muted-foreground">Leave blank to disable a limit. All values are % of your equity unless noted.</div>
          </div>
          {form.auto_pause_bots && <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--gain))] flex items-center gap-1">
            <Pause className="w-3 h-3" /> auto-pause on
          </span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <NumField label="Daily loss cap (%)" hint="Halt bots when today ≥ X% down" v={form.daily_loss_cap_pct} on={(v) => setForm({...form, daily_loss_cap_pct: v})} testid="risk-daily-cap" />
          <NumField label="Max drawdown (%)" hint="Halt bots when trailing DD hits X%" v={form.max_drawdown_pct} on={(v) => setForm({...form, max_drawdown_pct: v})} testid="risk-max-dd" />
          <NumField label="Max position (%)" hint="Cap any single symbol at X% of equity" v={form.max_position_pct} on={(v) => setForm({...form, max_position_pct: v})} testid="risk-max-pos" />
          <NumField label="Max open symbols" hint="Limit total concurrent tickers" v={form.max_symbols} on={(v) => setForm({...form, max_symbols: v})} testid="risk-max-syms" step="1" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.auto_pause_bots}
                   onChange={(e) => setForm({...form, auto_pause_bots: e.target.checked})}
                   data-testid="risk-auto-pause" />
            Auto-pause running bots when a limit trips
          </label>
          <Button onClick={save} disabled={saving} data-testid="risk-save-btn" className="rounded-full">
            {saving ? "Saving…" : "Save limits"}
          </Button>
        </div>
      </Card>

      {/* Broker failover */}
      <Card className="p-5 rounded-2xl" data-testid="broker-failover-editor">
        <div className="text-lg font-display font-semibold mb-1 flex items-center gap-2">
          <Activity className="w-4 h-4" /> Broker failover
        </div>
        <div className="text-xs text-muted-foreground mb-4">
          If your primary broker times out mid-order, Aanar automatically retries through the fallback route.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Primary</Label>
            <select value={failover.primary} onChange={(e) => setFailover({...failover, primary: e.target.value})}
                    data-testid="failover-primary"
                    className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              {["sim", "ibkr", "alpaca", "tradier"].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Fallback</Label>
            <select value={failover.fallback} onChange={(e) => setFailover({...failover, fallback: e.target.value})}
                    data-testid="failover-fallback"
                    className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              {["sim", "ibkr", "alpaca", "tradier"].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm flex-1">
              <input type="checkbox" checked={failover.auto_failover}
                     onChange={(e) => setFailover({...failover, auto_failover: e.target.checked})}
                     data-testid="failover-auto" />
              Auto-retry
            </label>
            <Button onClick={saveFailover} data-testid="failover-save" className="rounded-full">Save</Button>
          </div>
        </div>
      </Card>

      {/* Real-money gate */}
      <Card className={`p-5 rounded-2xl border-2 ${realMoney.enabled ? "border-[hsl(var(--loss))]" : "border-border"}`} data-testid="real-money-card">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-5 h-5" />
          <div className="text-lg font-display font-semibold">Real-money mode</div>
          {realMoney.enabled && (
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-[hsl(var(--loss))] text-white px-2 py-0.5 rounded-full" data-testid="rm-enabled-badge">
              LIVE
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mb-3 flex items-start gap-1.5">
          <AlertOctagon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Route bot + webhook orders to your connected live broker. Losses are real. A daily notional cap enforces a safety limit — Aanar rejects any real-money order that would exceed today's cap.
          </span>
        </div>
        {realMoney.enabled ? (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <div><span className="text-muted-foreground">Daily cap:</span> <span className="font-mono-tab">${realMoney.max_daily_notional_usd?.toLocaleString?.()}</span></div>
              <div className="text-xs text-muted-foreground">Enabled {new Date(realMoney.enabled_at).toLocaleString()}</div>
              {realMoney.has_webhook_secret && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  Webhook: <span className="font-mono">POST /api/real-money/webhook</span> — secret in Settings
                </div>
              )}
            </div>
            <Button variant="outline" onClick={disableRealMoney} data-testid="rm-disable-btn" className="rounded-full">
              Disable real money
            </Button>
          </div>
        ) : (
          <Button onClick={enableRealMoney} data-testid="rm-enable-btn" className="rounded-full">
            Enable real-money mode
          </Button>
        )}
      </Card>
    </div>
  );
}

const Stat = ({ label, val, cls = "" }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-xl md:text-2xl font-mono-tab mt-1 ${cls}`}>{val}</div>
  </div>
);

const NumField = ({ label, hint, v, on, testid, step = "0.01" }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" step={step} value={v} onChange={(e) => on(e.target.value)}
           placeholder="off" data-testid={testid} className="rounded-lg" />
    <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>
  </div>
);
